// src/components/Admin/Copies/SummaryReportModal.jsx
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
} from "@mui/material";
import Colors from "../../../assets/Variables/ColorsVariables";
import Button3px from "../../Generals/Buttons/3pxButton";
import IconComponent from "../../Generals/Icons/IconComponent";

const SummaryReportModal = ({ open, onClose, data }) => {
  if (!data) return null;

  const { signal, ticket, totalFollowers, ignoredCopies, copiedTickets, errors } =
    data;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          padding: 0,
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: "Plus Jakarta Sans, sans-serif",
            fontWeight: 700,
            fontSize: "18px",
            color: Colors.Secondary_900,
          }}
        >
          Summary Report
        </Typography>

        <Button
          onClick={onClose}
          sx={{
            minWidth: "32px",
            height: "32px",
            borderRadius: "999px",
            color: Colors.Gray_500,
            "&:hover": {
              backgroundColor: Colors.Gray_100,
            },
          }}
        >
          ×
        </Button>
      </DialogTitle>

      <DialogContent
        sx={{
          pt: 0,
          pb: 1.5,
        }}
      >
        {/* Subtítulo: Signal + Ticket */}
        <Typography
          sx={{
            mb: 2,
            fontSize: "13px",
            color: Colors.Secondary_700,
          }}
        >
          Signal: {signal} Ticket #{ticket}
        </Typography>

        {/* Card superior resumen principal */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 1,
            mb: 3,
            borderRadius: "12px",
            border: "1px solid #E2E8F0",
            backgroundColor: Colors.Gray_25 || "#F8FAFC",
            p: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 500,
                color: Colors.Secondary_600,
              }}
            >
              Total Followers
            </Typography>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 700,
                color: Colors.Secondary_900,
              }}
            >
              {totalFollowers}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 500,
                color: Colors.Secondary_600,
              }}
            >
              Ignored Copies
            </Typography>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 700,
                color: Colors.Secondary_900,
              }}
            >
              {ignoredCopies}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 500,
                color: Colors.Secondary_600,
              }}
            >
              Tickets Copiados
            </Typography>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 700,
                color: Colors.Secondary_900,
              }}
            >
              {copiedTickets}
            </Typography>
          </Box>
        </Box>

        {/* Título sección análisis */}
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 600,
            color: Colors.Secondary_800,
            mb: 1,
          }}
        >
          Analysis of possible errors
        </Typography>

        {/* Lista de posibles errores (cards/chips) */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {errors && errors.length > 0 ? (
            errors.map((err, idx) => (
              <Box
                key={`${err.follower}-${err.ticket}-${idx}`}
                sx={{
                  borderRadius: "10px",
                  border: "1px solid #E2E8F0",
                  backgroundColor: "#FFFFFF",
                  px: 1.5,
                  py: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  {/* Chips follower y ticket */}
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      px: 1,
                      py: 0.25,
                      borderRadius: "999px",
                      backgroundColor: Colors.Gray_100,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "10px",
                        fontWeight: 500,
                        color: Colors.Secondary_700,
                      }}
                    >
                      Follower: {err.follower}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      px: 1,
                      py: 0.25,
                      borderRadius: "999px",
                      backgroundColor: Colors.Gray_100,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "10px",
                        fontWeight: 500,
                        color: Colors.Secondary_700,
                      }}
                    >
                      Ticket: {err.ticket}
                    </Typography>
                  </Box>
                </Box>

                {/* Reason */}
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: Colors.Secondary_700,
                    mt: 0.5,
                  }}
                >
                  <strong>Reason:</strong> {err.reason}
                </Typography>
              </Box>
            ))
          ) : (
            <Box
              sx={{
                borderRadius: "10px",
                border: "1px dashed #CBD5E1",
                backgroundColor: "#F8FAFC",
                px: 1.5,
                py: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <IconComponent icon="info" size="18px" />
              <Typography
                sx={{
                  fontSize: "12px",
                  color: Colors.Secondary_600,
                }}
              >
                No issues detected for this signal ticket.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 1.5,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            borderRadius: "10px",
            borderColor: Colors.Gray_300,
            color: Colors.Secondary_700,
            px: 3,
          }}
        >
          Cancel
        </Button>

        <Button3px
          label="OK"
          HandleOnClick={onClose}
          color={Colors.Warning_50}
          backgroundColor={Colors.Error_500}
          height="40px"
        >
          OK
        </Button3px>
      </DialogActions>
    </Dialog>
  );
};

SummaryReportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.shape({
    signal: PropTypes.string,
    ticket: PropTypes.string,
    totalFollowers: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ignoredCopies: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    copiedTickets: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    errors: PropTypes.arrayOf(
      PropTypes.shape({
        follower: PropTypes.string,
        ticket: PropTypes.string,
        reason: PropTypes.string,
      })
    ),
  }),
};

export default SummaryReportModal;